import { useRef, useCallback, useEffect } from 'react'

type WorkerModule = 'sp800-22' | 'sp800-90b' | 'tunable-bias' | 'gaussian'

interface UseWorkerOptions {
  onProgress?: (msg: any) => void
  onComplete?: (result: any) => void
  onError?: (error: string) => void
}

export function useWorker(module: WorkerModule, options: UseWorkerOptions) {
  const workerRef = useRef<Worker | null>(null)

  const terminate = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate()
      workerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => terminate()
  }, [terminate])

  const run = useCallback(
    (data: ArrayBuffer, params: Record<string, unknown>) => {
      terminate()

      let worker: Worker
      switch (module) {
        case 'sp800-22':
          worker = new Worker(
            new URL('@/engine/workers/sp80022Worker.ts', import.meta.url),
            { type: 'module' }
          )
          break
        case 'sp800-90b':
          worker = new Worker(
            new URL('@/engine/workers/sp80090bWorker.ts', import.meta.url),
            { type: 'module' }
          )
          break
        case 'tunable-bias':
          worker = new Worker(
            new URL('@/engine/workers/tunableBiasWorker.ts', import.meta.url),
            { type: 'module' }
          )
          break
        case 'gaussian':
          worker = new Worker(
            new URL('@/engine/workers/gaussianWorker.ts', import.meta.url),
            { type: 'module' }
          )
          break
      }

      worker.onmessage = (e: MessageEvent) => {
        const msg = e.data
        switch (msg.type) {
          case 'progress':
            options.onProgress?.(msg)
            break
          case 'complete':
            options.onComplete?.(msg.result)
            break
          case 'error':
            options.onError?.(msg.error)
            break
        }
      }

      worker.onerror = (e) => {
        options.onError?.(e.message || 'Worker error')
      }

      workerRef.current = worker
      worker.postMessage({ type: 'run', ...params, data }, [data])
    },
    [module, options, terminate]
  )

  return { run, terminate, isRunning: workerRef.current !== null }
}
